import React, { useEffect, useMemo, useState } from "react";
import CommonFreeTextInput from "../../common/inputs/CommonFreeTextInput";
import { formatMoney, getNumber } from "../../../features/utils/formatMoney";
import CommonCollapsible from "../../common/CommonCollapsible";
import { clone } from "../../../features/utils/objClone";

export default function PassengerBreakdownCard({
  normalized,
  serviceFee, // per PAX service charge (same for all types)
  passengerCount,
  item, // { travelerType, breakDown: [{ name, amount }] }
  travellerLabel,
  travelerType,
  handleSaveBreakdown, // (travelerType, nextBreakDown[]) => void
}) {
  const [open, setOpen] = useState(false);
  const [localBreakdown, setLocalBreakdown] = useState([]);

  useEffect(() => {
    // if (!open) return;
    setLocalBreakdown(clone(item?.breakDown || []));
    // eslint-disable-next-line
  }, [open, item?.travelerType]);

  const globalBreakdown = useMemo(() => item?.breakDown || [], [item]);

  const hasGlobalQuoteValue = useMemo(() => {
    return (globalBreakdown || []).some((x) => Number(x?.amount || 0) > 0);
  }, [globalBreakdown]);

  const isDirty = useMemo(() => {
    const g = globalBreakdown || [];
    const l = localBreakdown || [];
    if (g.length !== l.length) return true;

    for (let i = 0; i < g.length; i++) {
      const ga = Number(g[i]?.amount || 0);
      const la = Number(l[i]?.amount || 0);
      if (ga !== la) return true;
    }
    return false;
  }, [globalBreakdown, localBreakdown]);

  const icon = useMemo(() => {
    switch (travelerType) {
      case "CHD":
      case "CNN":
        return "./flightChild.svg";
      case "INF":
      case "INS":
        return "./flightInfant.svg";
      default:
        return "./flightAdult.svg";
    }
  }, [travelerType]);

  // sum of breakdown per PAX (Change Fee + Fare Difference + Penality)
  const localChargesPerPax = useMemo(() => {
    return (localBreakdown || []).reduce((acc, cur) => {
      return acc + Number(cur?.amount || 0);
    }, 0);
  }, [localBreakdown]);

  const totalChargePerPax = useMemo(() => {
    return Number(localChargesPerPax || 0) + Number(serviceFee || 0);
  }, [localChargesPerPax, serviceFee]);

  const totalChargeForType = useMemo(() => {
    return totalChargePerPax * Number(passengerCount || 0);
  }, [totalChargePerPax, passengerCount]);

  const onChangeLocal = (i, v) => {
    const next = clone(localBreakdown || []);
    if (!next?.[i]) return;

    // ✅ numeric + non-negative
    const val = Math.max(0, Number(getNumber(v) || 0));
    next[i].amount = val;

    setLocalBreakdown(next);
  };

  const onSave = () => {
    if (!isDirty) {
      setOpen(false);
      return;
    }
    handleSaveBreakdown?.(item?.travelerType, localBreakdown);
    setOpen(false);
  };

  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-4 flex flex-col">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setOpen((s) => !s)}
      >
        <div className="font-semibold text-gray-900 flex items-center gap-1 ">
          <img
            src={icon}
            alt=""
            className={
              "w-8 h-8 object-contain mr-4 " +
              (icon === "./flightChild.svg" ? "mb-1" : "")
            }
          />
          <span>{travellerLabel?.[normalized] || normalized}</span>
          <div className="text-xs text-gray-500 font-medium">
            (x{passengerCount})
          </div>
        </div>

        {!hasGlobalQuoteValue ? (
          <button
            type="button"
            className="w-[150px] rounded-md border border-primary1 text-primary1 flex items-center justify-center h-10 text-sm font-semibold"
          >
            Add Quote
          </button>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-2 flex items-center gap-3">
            <div className="text-sm text-gray-600">Charge total</div>
            <div className="font-semibold text-gray-900">
              {formatMoney(totalChargeForType)}
            </div>
          </div>
        )}
      </div>

      {/* Collapsible */}
      <CommonCollapsible open={open}>
        <div className="w-full flex flex-col pt-4">
          <div className="space-y-3 border-t border-gray-100 pt-4">
            {(localBreakdown || []).map((obj, i) => (
              <div key={i} className="flex items-start justify-between gap-4">
                <div className="flex flex-col">
                  <div className="text-sm font-medium text-gray-800">
                    {obj?.name}
                    <span className="text-xs text-gray-400 ml-1">
                      (per PAX)
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">Charge</div>
                </div>

                <div className="w-[190px]">
                  <CommonFreeTextInput
                    label=""
                    placeholder="0"
                    value={String(obj?.amount?.toLocaleString() ?? "")}
                    onChange={(v) => onChangeLocal(i, v)}
                    className="!p-0"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Service charge */}
          <div className="mt-4 pt-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">Service Charge </div>
            <div className="font-semibold text-gray-900">
              {formatMoney(serviceFee)}
            </div>
          </div>

          {/* Totals (same UI blocks) */}
          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <div className="text-sm text-gray-600">
              Total charge per {travellerLabel?.[normalized] || normalized}
            </div>
            <div className="font-semibold text-gray-900">
              {formatMoney(totalChargePerPax)}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <div className="text-sm text-black font-bold">
              Total charge{" "}
              <span className="text-xs text-gray-400">
                (x{passengerCount} {travellerLabel?.[normalized] || normalized})
              </span>
            </div>
            <div className="font-semibold text-gray-900">
              {formatMoney(totalChargeForType)}
            </div>
          </div>

          {/* Save */}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!isDirty}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition
                ${
                  isDirty
                    ? "bg-primary1 text-white hover:opacity-90"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
            >
              Save
            </button>
          </div>
        </div>
      </CommonCollapsible>
    </div>
  );
}
